import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getClinicalReimbursementData, saveClinicalReimbursementForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import { trimData } from '../../../../../../utils/formatText';
import SkeletonBlock from '../../../SkeletonBlock';
import { flattenTransitionSubmitData } from '../../../formDataUtils';
import { CLINICAL_REIMBURSEMENT } from '../../../formQuestionsEnum';
import BeneficiaryNoticeProcessForm from './BeneficiaryNoticeProcessForm';
import CensusBreakdownForm from './CensusBreakdownForm';
import MedicareCertificationsForm from './MedicareCertificationsForm';
import StaffingForm from './StaffingForm';

const ClinicalReimbursementView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const clinicalReimbursementPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.clinicalReimbursementFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getClinicalReimbursementData(id, facilityId ?? 0)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(CLINICAL_REIMBURSEMENT).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [data, setFormData]);

  const submitForm = async (allData) => {
    const flattenData = flattenTransitionSubmitData(allData);
    const result = trimData(flattenData);

    await saveClinicalReimbursementForm({
      id,
      sellerFacilityId: facilityId,
      ...result,
    })
      .then((resp) => {
        enqueueSnackbar('Clinical Reimbursement data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Clinical Reimbursement data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      <CensusBreakdownForm permissions={clinicalReimbursementPermissions} />
      <StaffingForm permissions={clinicalReimbursementPermissions} />
      <BeneficiaryNoticeProcessForm permissions={clinicalReimbursementPermissions} />
      <MedicareCertificationsForm permissions={clinicalReimbursementPermissions} />
    </>
  );
});

export default ClinicalReimbursementView;

ClinicalReimbursementView.propTypes = {
  setFormData: PropTypes.func,
};
